import { patchState, signalStoreFeature, type, withMethods, withState } from '@ngrx/signals';
import { EMPTY, forkJoin, map, Observable, of, retry, switchMap, timer } from 'rxjs';
import { DestroyRef, inject } from '@angular/core';
import { UmmeldungService } from '../../service/ummeldung/ummeldung.service';
import { Produktdaten } from '../../../generated/model/produktdaten.model';
import { UmmeldungLockRequestParams } from '../../model/ummeldung.model';
import { catchError, expand } from 'rxjs/operators';
import { UmmeldungWizardData } from './models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UmmeldungState } from './ummeldung.store';

const LOCK_REFRESH_BUFFER_MS = 30_000;

function parseExpiresAt(expiresAt?: string | null): number | null {
  if (!expiresAt) {
    return null;
  }
  const expiresAtMs = Date.parse(expiresAt);
  return Number.isFinite(expiresAtMs) ? expiresAtMs : null;
}

export interface UmmeldungLockStoreState {
  lockExpiresAt: Date | null;
}

const state: UmmeldungLockStoreState = {
  lockExpiresAt: null,
};

export function withUmmeldungLocking() {
  return signalStoreFeature(
    { state: type<UmmeldungState>() },
    withState(state),
    withMethods((store, service = inject(UmmeldungService), destroyRef = inject(DestroyRef)) => {
      /**
       * Requests a lock for a single meter point (retries up to 3 times on failure).
       */
      function _acquireLock$(produktdaten: Produktdaten): Observable<number | null> {
        const lockRequestParams: UmmeldungLockRequestParams = {
          zaehlpunktnummer: produktdaten.zaehlpunktnummer,
          vertragsnummer: produktdaten.vertragsnummer,
        };

        return service.acquireUmmeldungLock(lockRequestParams).pipe(
          retry({
            count: 3,
            delay: 5_000,
          }),
          map(response => parseExpiresAt(response.expiresAt)),
          catchError(_ => {
            return of(null);
          }),
        );
      }

      /**
       * Evaluates lock expirations, updates `lockExpiresAt` in store, and computes the next refresh delay.
       */
      function _processLockExpirations(expiresAtValues: (number | null)[]): number {
        const validExpiresAtValues = expiresAtValues.filter(expiresAt => expiresAt !== null);
        if (validExpiresAtValues.length !== expiresAtValues.length || validExpiresAtValues.length === 0) {
          patchState(store, { lockExpiresAt: null });
          // improve error handling after launch of ummeldung mvp - see IKP-4355
          return LOCK_REFRESH_BUFFER_MS;
        }
        const expiresAtMs = Math.min(...validExpiresAtValues);
        const lockExpiresAt = new Date(expiresAtMs);
        const refreshInMs = Math.max(LOCK_REFRESH_BUFFER_MS, expiresAtMs - Date.now() - LOCK_REFRESH_BUFFER_MS);
        patchState(store, { lockExpiresAt });
        return refreshInMs;
      }

      /**
       * Requests locks in parallel for all meter points and returns the next refresh delay.
       */
      function _acquireLocks$(): Observable<number> {
        const produktdaten = store.activeUmmeldung()?.produktdaten;
        if (!produktdaten || produktdaten.length === 0) {
          return EMPTY;
        }
        const lockRequests = produktdaten.map(zaehlpunkt => _acquireLock$(zaehlpunkt));
        return forkJoin(lockRequests).pipe(map(_processLockExpirations));
      }

      return {
        /**
         * Initializes wizard state and starts recurring lock renewal until store destruction.
         */
        initialize(data: UmmeldungWizardData): void {
          patchState(store, {
            ...data,
            lockExpiresAt: null,
            loading: false,
            error: null,
          });

          _acquireLocks$()
            .pipe(
              expand(refreshInMs => {
                return timer(refreshInMs).pipe(switchMap(() => _acquireLocks$()));
              }),
              takeUntilDestroyed(destroyRef),
            )
            .subscribe();
        },
      };
    }),
  );
}
